import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Check, X, Inbox, ArrowUpRight, Trash2, Search, Link2, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const TABS = [
  { value: "pending", label: "In attesa" },
  { value: "approved", label: "Approvati" },
  { value: "rejected", label: "Rifiutati" },
];

export default function StaffClients() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [proOnly, setProOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);
  const [magicLink, setMagicLink] = useState(null); // { client_name, url, expires_at } | null
  const [magicWorkingId, setMagicWorkingId] = useState(null);
  const navigate = useNavigate();

  const generateMagicLink = async (client) => {
    setMagicWorkingId(client.id);
    try {
      // v1.34: this button used to POST /upload-link/generate (24h TTL,
      // 10 files, 500MB each) which broke the "permanent link" promise
      // — clients pinned the URL in their chat and it kept expiring on
      // them. Switched to the permanent per-client link (idempotent GET).
      const { data } = await api.get(
        `/staff/clients/${client.id}/upload-link`,
      );
      setMagicLink({
        client_name: data.client_name || client.name_salone,
        url: data.url,
        permanent: true,
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMagicWorkingId(null);
    }
  };

  const copyMagicLink = async () => {
    if (!magicLink?.url) return;
    try {
      await navigator.clipboard.writeText(magicLink.url);
      toast.success("Link copiato negli appunti");
    } catch (_) {
      toast.error("Impossibile copiare (permessi appunti negati)");
    }
  };

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/client-requests`, {
        params: { status },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  // Plan breakdown for the "Approvati" tab. We classify Free clients in 2
  // buckets based on whether they've completed the positioning step that
  // unlocks the AI tools — this matches the same definition used by Brevo
  // (PROFILE_COMPLETE = has salone + has positioning).
  const isProfileComplete = (u) =>
    Boolean(u.profile_setup?.has_salone && u.profile_setup?.has_positioning);
  const planBreakdown = items.reduce(
    (acc, u) => {
      if (u.plan === "pro") acc.pro += 1;
      else if (isProfileComplete(u)) acc.freeUnlocked += 1;
      else acc.freeBase += 1;
      return acc;
    },
    { freeBase: 0, freeUnlocked: 0, pro: 0 },
  );

  // Client-side filter: salone name, email, referente name/cognome.
  // Server-side search would require a new API endpoint — overkill for
  // the typical agency size (<200 clients).
  const q = query.trim().toLowerCase();
  // Pro filter is only meaningful on the "approved" tab (pending/rejected
  // clients don't have a stable plan yet).
  const proFilterActive = proOnly && tab === "approved";
  let filtered = proFilterActive ? items.filter((u) => u.plan === "pro") : items;
  if (q) {
    filtered = filtered.filter((u) => {
      const haystack = [
        u.name_salone,
        u.email,
        u.referente_nome,
        u.referente_cognome,
        `${u.referente_nome || ""} ${u.referente_cognome || ""}`.trim(),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  const act = async (id, action) => {
    setWorkingId(id);
    try {
      await api.post(`/staff/client-requests/${id}`, { action });
      toast.success(action === "approve" ? "Cliente approvato" : "Cliente rifiutato");
      await load(tab);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const deleteClient = async (u) => {
    const label = u.name_salone || u.email;
    if (!window.confirm(
      `Eliminare definitivamente "${label}"?\n\nVerranno cancellati anche tutti i suoi messaggi, idee storie e post programmati.\n\nQuesta azione NON è reversibile.`
    )) return;
    const typed = window.prompt(
      `Per confermare scrivi: ELIMINA\n\n(stai per cancellare ${label})`
    );
    if ((typed || "").trim().toUpperCase() !== "ELIMINA") {
      toast.info("Eliminazione annullata");
      return;
    }
    setWorkingId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      const d = data?.deleted || {};
      toast.success(
        `Eliminato "${label}" (msg: ${d.messages || 0}, idee: ${d.story_ideas || 0}, post: ${d.scheduled_posts || 0})`
      );
      await load(tab);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div data-testid="staff-clients-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Clienti · Saloni
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Gestione clienti
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Approva i saloni che si sono registrati sulla piattaforma. Finché non li approvi, il
          cliente vedrà la dashboard con un avviso di "in attesa di approvazione".
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 rounded-full bg-white border border-[var(--mp-border)] p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.value}
              data-testid={`clients-tab-${t.value}`}
              onClick={() => setTab(t.value)}
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${
                tab === t.value
                  ? "bg-[var(--mp-ink)] text-white"
                  : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--mp-muted)] pointer-events-none" />
            <input
              type="search"
              data-testid="clients-search-input"
              placeholder="Cerca per salone, email, referente…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-11 pl-9 pr-9 rounded-full bg-white border border-[var(--mp-border)] text-sm focus:outline-none focus:border-[var(--mp-ink)] transition-colors"
            />
            {query && (
              <button
                type="button"
                data-testid="clients-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full grid place-items-center text-[var(--mp-muted)] hover:bg-[var(--mp-dash-bg)]"
                aria-label="Pulisci ricerca"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {tab === "approved" && (
            <button
              type="button"
              data-testid="clients-filter-pro"
              onClick={() => setProOnly((v) => !v)}
              aria-pressed={proOnly}
              className={`h-11 px-4 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                proOnly
                  ? "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]"
                  : "bg-white text-[var(--mp-ink)] border-[var(--mp-border)] hover:border-[var(--mp-ink)]"
              }`}
              title={proOnly ? "Mostra tutti i clienti" : "Mostra solo clienti Pro"}
            >
              Pro
              <span
                className={`ml-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${
                  proOnly ? "bg-white/20 text-white" : "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]"
                }`}
              >
                {planBreakdown.pro}
              </span>
            </button>
          )}
        </div>
      </div>

      {tab === "approved" && !loading && items.length > 0 && (
        <div
          data-testid="clients-plan-breakdown"
          className="flex flex-wrap items-stretch gap-2"
        >
          <CountChip
            label="Free base"
            sublabel="solo email/password"
            value={planBreakdown.freeBase}
            tone="muted"
            testid="count-free-base"
          />
          <CountChip
            label="Free sbloccato"
            sublabel="ha completato il posizionamento"
            value={planBreakdown.freeUnlocked}
            tone="yellow"
            testid="count-free-unlocked"
          />
          <CountChip
            label="Pro"
            sublabel="piano attivo"
            value={planBreakdown.pro}
            tone="ink"
            testid="count-pro"
          />
        </div>
      )}

      <div className="rounded-3xl border border-[var(--mp-border)] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[var(--mp-muted)]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[var(--mp-muted)] flex flex-col items-center gap-3" data-testid="clients-empty-state">
            <Inbox className="h-6 w-6" />
            {proFilterActive && q
              ? `Nessun cliente Pro trovato per «${query}».`
              : proFilterActive
                ? "Nessun cliente Pro."
                : q
                  ? `Nessun cliente trovato per «${query}».`
                  : `Nessun cliente ${tab === "pending" ? "in attesa" : tab === "approved" ? "approvato" : "rifiutato"}.`}
          </div>
        ) : (
          <ul className="divide-y divide-[var(--mp-border)]">
            {filtered.map((u) => (
              <li
                key={u.id}
                data-testid={`client-row-${u.id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-4 p-5"
              >
                <button
                  onClick={() => navigate(`/dashboard/clienti/${u.id}`)}
                  data-testid={`client-open-${u.id}`}
                  className="flex-1 min-w-0 text-left group"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg tracking-tight truncate group-hover:underline">
                      {u.name_salone || u.email}
                    </div>
                    <PlanBadge plan={u.plan} testid={`client-plan-${u.id}`} />
                  </div>
                  <div className="text-sm text-[var(--mp-muted)] truncate">
                    {u.referente_nome} {u.referente_cognome} · {u.email}
                  </div>
                  {u.created_at && (
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mp-muted)] mt-1">
                      Registrato il {new Date(u.created_at).toLocaleString("it-IT")}
                    </div>
                  )}
                </button>
                <StatusBadge status={u.status} />
                {tab === "pending" ? (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      data-testid={`client-approve-${u.id}`}
                      disabled={workingId === u.id}
                      onClick={() => act(u.id, "approve")}
                      className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                    >
                      <Check className="h-4 w-4 mr-1" /> Approva
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`client-reject-${u.id}`}
                      disabled={workingId === u.id}
                      onClick={() => act(u.id, "reject")}
                      className="rounded-full border-[var(--mp-ink)]"
                    >
                      <X className="h-4 w-4 mr-1" /> Rifiuta
                    </Button>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`client-delete-${u.id}`}
                        disabled={workingId === u.id}
                        onClick={() => deleteClient(u)}
                        title="Elimina definitivamente"
                        className="rounded-full border-[var(--mp-ink)] text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      data-testid={`client-open-btn-${u.id}`}
                      onClick={() => navigate(`/dashboard/clienti/${u.id}`)}
                      className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                    >
                      Apri scheda <ArrowUpRight className="h-4 w-4 ml-1" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`client-magic-link-${u.id}`}
                      disabled={magicWorkingId === u.id}
                      onClick={() => generateMagicLink(u)}
                      title="Genera un magic link per far caricare video pesanti al cliente (24h)"
                      className="rounded-full border-[var(--mp-ink)]"
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      {magicWorkingId === u.id ? "..." : "Magic link"}
                    </Button>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`client-delete-${u.id}`}
                        disabled={workingId === u.id}
                        onClick={() => deleteClient(u)}
                        title="Elimina definitivamente"
                        className="rounded-full border-[var(--mp-ink)] text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Magic link result dialog */}
      <Dialog open={!!magicLink} onOpenChange={(o) => !o && setMagicLink(null)}>
        <DialogContent
          data-testid="magic-link-dialog"
          className="max-w-md rounded-3xl"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              📌 Link upload personale
            </DialogTitle>
            <DialogDescription>
              Link permanente di{" "}
              <strong>{magicLink?.client_name}</strong>: puoi condividerlo dove
              vuoi (WhatsApp, SMS, chat) e appuntarlo nella conversazione. Il
              cliente lo usa per caricare video e foto direttamente su Dropbox,
              senza login e senza limiti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1">
                Link permanente · non scade mai
              </div>
              <div
                data-testid="magic-link-url"
                className="text-sm font-mono break-all text-[var(--mp-ink)]"
              >
                {magicLink?.url}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={copyMagicLink}
                data-testid="magic-link-copy-btn"
                className="flex-1 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copia link
              </Button>
              <Button
                variant="outline"
                onClick={() => setMagicLink(null)}
                data-testid="magic-link-close-btn"
                className="rounded-full border-[var(--mp-ink)]"
              >
                Chiudi
              </Button>
            </div>
            <div className="text-[11px] text-[var(--mp-muted)] leading-relaxed">
              📌 <strong>Link personale permanente</strong> — non scade mai.
              Il cliente può caricarci quanti file vuole, senza limiti di
              dimensione. Ricordagli di caricare <strong>un video alla
              volta</strong> (chiudere e riaprire per il video successivo)
              così le clip di video diversi non si mischiano.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CountChip({ label, sublabel, value, tone = "muted", testid }) {
  const toneCls =
    tone === "ink"
      ? "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]"
      : tone === "yellow"
        ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)]"
        : "bg-white text-[var(--mp-ink)] border-[var(--mp-border)]";
  const subCls = tone === "ink" ? "text-white/70" : "text-[var(--mp-muted)]";
  return (
    <div
      data-testid={testid}
      className={`flex-1 min-w-[160px] rounded-2xl border ${toneCls} p-3 flex items-center gap-3`}
    >
      <div className="text-3xl font-display tracking-tighter leading-none tabular-nums">
        {value}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold leading-tight">{label}</div>
        <div className={`text-[10px] uppercase tracking-[0.18em] leading-tight mt-0.5 ${subCls}`}>
          {sublabel}
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    pending: "bg-[var(--mp-yellow)] text-[var(--mp-ink)]",
    approved: "bg-[var(--mp-ink)] text-white",
    rejected: "bg-[var(--mp-primary)] text-white",
  };
  const label = {
    pending: "In attesa",
    approved: "Approvato",
    rejected: "Rifiutato",
  };
  return (
    <Badge
      className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${map[status] || ""}`}
    >
      {label[status] || status}
    </Badge>
  );
}

function PlanBadge({ plan, testid }) {
  const isPro = (plan || "").toLowerCase() === "pro";
  return (
    <span
      data-testid={testid}
      title={isPro ? "Piano Pro" : "Piano Free"}
      className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] border ${
        isPro
          ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)]"
          : "bg-stone-100 text-stone-600 border-stone-300"
      }`}
    >
      {isPro ? "Pro" : "Free"}
    </span>
  );
}
